import {
  AddressCategory,
  addressCategoryToField,
  AddressFields,
  AddressSuggestion,
  DependencyError,
  html,
  LookupItem,
  markText,
  render
} from '@cumu/shared';
import type { AutoCompleteElement } from '@github/auto-complete-element';
import { isSessionExpiredResponse } from '../session';
import { dispatchChange } from './util';

export interface AutoCompleteResult {
  suggestions: LookupItem[];
}

export interface AutoCompleteProvideEventDetail {
  relatedTarget: HTMLInputElement;
  provide: (result: Promise<AutoCompleteResult>) => void;
}

export class AutoCompleteBehaviorElement extends HTMLElement {
  declare disconnectController: AbortController;
  abortResolveController?: AbortController;

  get autocomplete(): AutoCompleteElement | null {
    return this.closest('auto-complete') as AutoCompleteElement | null;
  }

  connectedCallback() {
    this.disconnectController = new AbortController();
    const signal = this.disconnectController.signal;

    const autocomplete = this.autocomplete;
    if (!autocomplete) {
      return;
    }

    // the github auto-complete element always sets autocomplete to off, which may not be appropriate.
    if (this.hasAttribute('autocomplete')) {
      requestAnimationFrame(() =>
        autocomplete.inputElement?.setAttribute(
          'autocomplete',
          this.getAttribute('autocomplete') ?? 'off'
        )
      );
    }

    if (autocomplete.src === '/use-events') {
      autocomplete.fetchResult = dispatchAutoCompleteProvide;
    }

    autocomplete.inputElement?.addEventListener(
      'blur',
      () => {
        // Close the dropdown on blur.
        // Timeout ensures this doesn't interfere with blurs that occur when selecting a suggestion.
        setTimeout(() => {
          if (autocomplete.open) {
            autocomplete.open = false;
          }
        }, 300); // do not reduce without testing quickly toggling between autocomplete suggestions with mouse.
      },
      { signal }
    );

    autocomplete.addEventListener(
      'auto-complete-change',
      () => {
        if (autocomplete.inputElement?.value.endsWith(' ')) {
          // trigger input event to fetch new suggestions
          autocomplete.inputElement.dispatchEvent(
            new CustomEvent('input', { bubbles: true })
          );
        }
      },
      { signal }
    );

    const relatedInput = this.hasAttribute('for')
      ? document.getElementById(this.getAttribute('for')!)
      : this.firstElementChild;
    if (
      relatedInput instanceof HTMLInputElement ||
      relatedInput instanceof HTMLTextAreaElement
    ) {
      const relatedInputKey: 'id' | 'title' =
        (this.getAttribute('for-key') as 'id' | 'title') ?? 'id';
      autocomplete.addEventListener(
        'combobox-commit',
        ({ target }: Event) => {
          this.abortResolveController?.abort();
          if (!(target instanceof HTMLElement)) {
            return;
          }
          let data = JSON.parse(
            target.getAttribute('data-autocomplete-data')!
          ) as { id: string; title: string } | AddressSuggestion;
          if ('magicKey' in data) {
            const autocomplete = this.autocomplete!;
            const input = autocomplete.inputElement!;
            const suggestion = data.text;
            const params = new URLSearchParams({
              q: data.text,
              'magic-key': data.magicKey,
              'for-storage': 'true'
            });
            data = { id: '', title: '' };
            const resolveAddress = async () => {
              input.labels![0].insertAdjacentHTML(
                'beforeend',
                render(
                  html`<span class="spinner-border color-fg-muted ml-2"></span>`
                )
              );
              const spinner = input.labels![0].lastElementChild!;
              this.abortResolveController = new AbortController();
              const signal = this.abortResolveController.signal;
              const response = await fetch(
                `/api/addresses/candidates?${params}`,
                { signal }
              );
              spinner.remove();
              if (isSessionExpiredResponse(response)) {
                return;
              }
              if (signal.aborted) {
                return;
              }
              if (!response.ok) {
                throw new DependencyError(response);
              }
              const [candidate] = (await response.json()) as AddressFields[];
              const category = new URL(
                autocomplete.src,
                location.href
              ).searchParams.get('category') as AddressCategory;
              const field = addressCategoryToField[category];
              const id = candidate[field];
              const title = suggestion;
              input.value = relatedInputKey === 'id' ? title : id;
              relatedInput.value = relatedInputKey === 'id' ? id : title;
              relatedInput.dataset.displayValue =
                relatedInputKey === 'id' ? title : id;
              dispatchChange(input);
            };
            resolveAddress();
          }
          const { id, title } = data;
          relatedInput.value = relatedInputKey === 'id' ? id : title;
          relatedInput.dataset.displayValue =
            relatedInputKey === 'id' ? title : id;
          target.setAttribute(
            'data-autocomplete-value',
            relatedInputKey === 'id' ? title : id
          );
        },
        { signal, capture: true }
      );

      autocomplete.inputElement?.addEventListener(
        'change',
        () => {
          const current = autocomplete.inputElement!.value;
          if (current !== relatedInput.dataset.displayValue) {
            autocomplete.inputElement!.value = '';
            relatedInput.value = '';
            relatedInput.dataset.displayValue = '';
          }
        },
        { signal }
      );

      // make sure the input element can be searched.
      const maxLength = autocomplete.inputElement?.maxLength ?? 100;
      if (maxLength < 20) {
        autocomplete.inputElement!.addEventListener(
          'focus',
          () => (autocomplete.inputElement!.maxLength = 30),
          { signal, capture: true }
        );
        autocomplete.inputElement!.addEventListener(
          'blur',
          () => {
            autocomplete.inputElement!.maxLength = maxLength;
          },
          { signal, capture: true }
        );
      }
    }
  }

  disconnectedCallback() {
    this.disconnectController.abort();
  }
}

async function dispatchAutoCompleteProvide(
  this: AutoCompleteElement,
  _url: URL
): Promise<string> {
  const provided: Promise<AutoCompleteResult>[] = [];
  const provide = (result: Promise<AutoCompleteResult>) => {
    provided.push(result);
  };
  const relatedTarget = this.inputElement!;
  this.dispatchEvent(
    new CustomEvent<AutoCompleteProvideEventDetail>('auto-complete-provide', {
      bubbles: true,
      detail: { relatedTarget, provide }
    })
  );
  const results = await Promise.all(provided);
  return render(
    results
      .flatMap(x => x.suggestions)
      .map(
        ({ id, title }) =>
          html`<li
            role="option"
            class="autocomplete-item"
            data-autocomplete-value="${id}"
            data-autocomplete-data="${JSON.stringify({ id, title })}"
          >
            ${markText(title, relatedTarget.value)}
          </li>`
      )
  );
}

declare global {
  interface Window {
    AutoCompleteBehaviorElement: typeof AutoCompleteBehaviorElement;
  }
  interface HTMLElementTagNameMap {
    'auto-complete-behavior': AutoCompleteBehaviorElement;
  }

  interface ElementEventMap {
    'auto-complete-provide': CustomEvent<AutoCompleteProvideEventDetail>;
  }

  interface WindowEventMap {
    'auto-complete-provide': CustomEvent<AutoCompleteProvideEventDetail>;
  }
}

if (!window.customElements.get('auto-complete-behavior')) {
  window.AutoCompleteBehaviorElement = AutoCompleteBehaviorElement;
  window.customElements.define(
    'auto-complete-behavior',
    AutoCompleteBehaviorElement
  );
}
